import Vue from "vue";
import VueRouter from "vue-router";
import store from "../store/index.js";
import Login from "../views/pages/Login.vue";
import NotFound from "../views/NotFound.vue";
import Unauthorized from "../views/Unauthorized.vue";

Vue.use(VueRouter);

const routes = [
  {
    path: "/",
    name: "Login",
    meta: {
      title: "Login",
      requiresAuth: false,
    },
    component: Login,
  },
  {
    path: "/dashboard",
    name: "DashboardMain",
    meta: {
      title: "Dashboard",
      requiresAuth: true,
    },
    component: () => import("../views/pages/dashboard/DashboardMain.vue"),
  },
  {
    path: "/asset",
    name: "AssetList",
    meta: {
      title: "List Assets",
      requiresAuth: true,
    },
    component: () => import("../views/pages/asset/AssetList.vue"),
  },
  {
    path: "/asset/create",
    name: "AssetCreate",
    meta: {
      title: "New Asset",
      requiresAuth: true,
      endpoint: "asset",
      baseRoute: "AssetList",
    },
    component: () => import("../views/pages/asset/AssetForm.vue"),
  },
  {
    path: "/asset/:id/edit",
    name: "AssetEdit",
    meta: {
      title: "View Asset",
      requiresAuth: true,
      endpoint: "asset",
      baseRoute: "AssetList",
    },
    component: () => import("../views/pages/asset/AssetForm.vue"),
  },

  {
    path: "/bank",
    name: "BankList",
    meta: {
      title: "List Bank",
      requiresAuth: true,
    },
    component: () => import("../views/pages/bank/BankList.vue"),
  },
  {
    path: "/bank/create",
    name: "BankCreate",
    meta: {
      title: "New Bank",
      requiresAuth: true,
      endpoint: "bank",
      baseRoute: "BankList",
    },
    component: () => import("../views/pages/bank/BankForm.vue"),
  },
  {
    path: "/bank/:id/edit",
    name: "BankEdit",
    meta: {
      title: "View Bank",
      requiresAuth: true,
      endpoint: "bank",
      baseRoute: "BankList",
    },
    component: () => import("../views/pages/bank/BankForm.vue"),
  },

  {
    path: "/user",
    name: "UserList",
    meta: {
      title: "List Users",
      requiresAuth: true,
    },
    component: () => import("../views/pages/user/UserList.vue"),
  },
  {
    path: "/user/create",
    name: "UserCreate",
    meta: {
      title: "New User",
      requiresAuth: true,
      endpoint: "user",
      baseRoute: "UserList",
    },
    component: () => import("../views/pages/user/UserForm.vue"),
  },
  {
    path: "/user/:id/edit",
    name: "UserEdit",
    meta: {
      title: "View User",
      requiresAuth: true,
      endpoint: "user",
      baseRoute: "UserList",
    },
    component: () => import("../views/pages/user/UserForm.vue"),
  },
  {
    path: "/member",
    name: "MemberList",
    meta: {
      title: "Search Member",
      requiresAuth: true,
    },
    component: () => import("../views/pages/member/MemberList.vue"),
  },
  {
    path: "/member/merge",
    name: "MemberMerge",
    meta: {
      title: "Merge Member",
      requiresAuth: true,
      baseRoute: "MemberList",
    },
    component: () => import("../views/pages/member/MemberMerge.vue"),
  },
  {
    path: "/member/create",
    name: "MemberCreate",
    meta: {
      title: "New Member",
      requiresAuth: true,
      endpoint: "member",
      baseRoute: "MemberList",
    },
    component: () => import("../views/pages/member/MemberForm.vue"),
  },
  {
    path: "/member/:memberId",
    name: "MemberProfile",
    meta: {
      title: "Member Profile",
      requiresAuth: true,
      endpoint: "member",
      baseRoute: "MemberList",
    },
    component: () => import("../views/pages/member/MemberProfile.vue"),
    children: [
      {
        path: "history",
        name: "MemberTransactionHistory",
        meta: {
          title: "Member Transaction History",
          requiresAuth: true,
          endpoint: (route) => "member/" + route.params.memberId + "/history",
          baseRoute: "MemberList",
        },
        component: () =>
          import("../views/pages/member/MemberTransactionHistory.vue"),
      },
      {
        path: "account/create",
        name: "AccountCreate",
        meta: {
          title: "New Account",
          requiresAuth: true,
          endpoint: (route) => "member/" + route.params.memberId + "/account",
          baseRoute: "MemberList",
        },
        component: () =>
          import("../views/pages/member/account/MemberAccountForm.vue"),
      },
      {
        path: "account/:id/edit",
        name: "AccountEdit",
        meta: {
          title: "View Account",
          requiresAuth: true,
          endpoint: (route) => "member/" + route.params.memberId + "/account",
          baseRoute: "MemberList",
        },
        component: () =>
          import("../views/pages/member/account/MemberAccountForm.vue"),
      },
      {
        path: "account/:id/delete",
        name: "AccountDelete",
        meta: {
          title: "Delete Account",
          requiresAuth: true,
          endpoint: (route) => "member/" + route.params.memberId + "/account",
          baseRoute: "MemberList",
        },
        component: () =>
          import("../views/pages/member/account/DeleteMemberAccountForm.vue"),
      },
      {
        path: "account/:id/transfer",
        name: "AccountTransfer",
        meta: {
          title: "Transfer Account",
          requiresAuth: true,
          endpoint: (route) => "member/" + route.params.memberId + "/account",
          baseRoute: "MemberList",
        },
        component: () =>
          import("../views/pages/member/MemberTransfer.vue"),
      },
      {
        path: "bank-account/merge",
        name: "MemberBankMerge",
        meta: {
          title: "Merge Member Bank",
          requiresAuth: true,
          endpoint: (route) =>
            "member/" + route.params.memberId + "/bank-account",
          baseRoute: "MemberList",
        },
        component: () =>
          import(
            "../views/pages/member/bank-account/MergeMemberBankAccountForm.vue"
          ),
      },
      {
        path: "bank-account/create",
        name: "MemberBankCreate",
        meta: {
          title: "New Member Bank",
          requiresAuth: true,
          endpoint: (route) =>
            "member/" + route.params.memberId + "/bank-account",
          baseRoute: "MemberList",
        },
        component: () =>
          import(
            "../views/pages/member/bank-account/MemberBankAccountForm.vue"
          ),
      },
      {
        path: "bank-account/:id/edit",
        name: "MemberBankEdit",
        meta: {
          title: "View Member Bank",
          requiresAuth: true,
          endpoint: (route) =>
            "member/" + route.params.memberId + "/bank-account",
          baseRoute: "MemberList",
        },
        component: () =>
          import(
            "../views/pages/member/bank-account/MemberBankAccountForm.vue"
          ),
      },
      {
        path: "bank-account/:id/transfer",
        name: "MemberBankTransfer",
        meta: {
          title: "Transfer Member Bank",
          requiresAuth: true,
          endpoint: (route) =>
            "member/" + route.params.memberId + "/bank-account",
          baseRoute: "MemberList",
        },
        component: () =>
          import(
            "../views/pages/member/MemberTransfer.vue"
          ),
      },
      {
        path: "bank-account/:id/delete",
        name: "MemberBankDelete",
        meta: {
          title: "Delete Member Bank",
          requiresAuth: true,
          endpoint: (route) =>
            "member/" + route.params.memberId + "/bank-account",
          baseRoute: "MemberList",
        },
        component: () =>
          import(
            "../views/pages/member/bank-account/DeleteMemberBankAccountForm.vue"
          ),
      },
    ],
  },
  {
    path: "/member/:id/edit",
    name: "MemberEdit",
    meta: {
      title: "View Member",
      requiresAuth: true,
      endpoint: "member",
      baseRoute: "MemberList",
    },
    component: () => import("../views/pages/member/MemberForm.vue"),
  },
  {
    path: "/product",
    name: "ProductList",
    meta: {
      title: "List Product",
      requiresAuth: true,
    },
    component: () => import("../views/pages/product/ProductList.vue"),
  },
  {
    path: "/product/create",
    name: "ProductCreate",
    meta: {
      title: "New Product",
      requiresAuth: true,
      endpoint: "product",
      baseRoute: "ProductList",
    },
    component: () => import("../views/pages/product/ProductForm.vue"),
  },
  {
    path: "/product/:id/edit",
    name: "ProductEdit",
    meta: {
      title: "View Product",
      requiresAuth: true,
      endpoint: "product",
      baseRoute: "ProductList",
    },
    component: () => import("../views/pages/product/ProductForm.vue"),
  },
  {
    path: "/bank-account",
    name: "BankAccountList",
    meta: {
      title: "List Bank Account",
      requiresAuth: true,
    },
    component: () => import("../views/pages/bank-account/BankAccountList.vue"),
  },
  {
    path: "/bank-account/create",
    name: "BankAccountCreate",
    meta: {
      title: "New Bank Account",
      requiresAuth: true,
      endpoint: "bank-account",
      baseRoute: "BankAccountList",
    },
    component: () => import("../views/pages/bank-account/BankAccountForm.vue"),
  },
  {
    path: "/bank-account/:bankId",
    name: "BankAccountDetail",
    meta: {
      title: "Bank Detail",
      requiresAuth: true,
      endpoint: "bank-account",
      baseRoute: "BankAccountList",
    },
    component: () =>
      import("../views/pages/bank-account/BankAccountDetail.vue"),
    children: [
      {
        path: "transaction",
        name: "BankTransactionList",
        meta: {
          title: "List Transaction",
          requiresAuth: true,
          endpoint: (route) =>
            "bank-account/" + route.params.bankId + "/transaction",
          baseRoute: "BankList",
        },
        component: () =>
          import("../views/pages/bank-transaction/BankTransactionList.vue"),
      },
      {
        path: "transaction/create",
        name: "BankTransactionCreate",
        meta: {
          title: "Create Transaction",
          requiresAuth: true,
          endpoint: (route) =>
            "bank-account/" + route.params.bankId + "/transaction",
          baseRoute: "BankTransactionList",
        },
        component: () =>
          import("../views/pages/bank-transaction/BankTransactionForm.vue"),
      },
      {
        path: "transaction/:id/:transferTo?",
        name: "BankTransactionEdit",
        meta: {
          title: "View Transaction",
          requiresAuth: true,
          endpoint: (route) =>
            "bank-account/" + route.params.bankId + "/transaction",
          baseRoute: "BankTransactionList",
        },
        component: () =>
          import("../views/pages/bank-transaction/BankTransactionForm.vue"),
      },
    ],
  },
  {
    path: "/bank-account/:id/edit",
    name: "BankAccountEdit",
    meta: {
      title: "View Bank Account",
      requiresAuth: true,
      endpoint: "bank-account",
      baseRoute: "BankAccountList",
    },
    component: () => import("../views/pages/bank-account/BankAccountForm.vue"),
  },
  {
    path: "/deposit",
    name: "DepositList",
    meta: {
      title: "List Deposit",
      requiresAuth: true,
    },
    component: () => import("../views/pages/deposit/DepositList.vue"),
  },
  {
    path: "/deposit/create/:accountId?",
    name: "DepositCreate",
    meta: {
      title: "Create Deposit",
      requiresAuth: true,
      endpoint: "deposit",
      baseRoute: "DepositList",
    },
    component: () => import("../views/pages/deposit/DepositForm.vue"),
  },
  // {
  //   path: "/deposit/placeholder",
  //   name: 'DepositPlaceholder',
  //   meta: {
  //     title: 'Create Deposit Placeholder',
  //     requiresAuth: true,
  //     endpoint: 'deposit',
  //     baseRoute: 'DepositList',
  //   },
  //   component: () => import("../views/pages/deposit/DepositPlaceholder.vue")
  // },
  {
    path: "/deposit/:id/edit",
    name: "DepositEdit",
    meta: {
      title: "View Deposit",
      requiresAuth: true,
      endpoint: "deposit",
      baseRoute: "DepositList",
    },
    component: () => import("../views/pages/deposit/DepositForm.vue"),
  },
  {
    path: "/bonus",
    name: "BonusList",
    meta: {
      title: "List Bonus",
      requiresAuth: true,
    },
    component: () => import("../views/pages/bonus/BonusList.vue"),
  },
  {
    path: "/bonus/create",
    name: "BonusCreate",
    meta: {
      title: "Create Bonus",
      requiresAuth: true,
      endpoint: "bonus",
      baseRoute: "BonusList",
    },
    component: () => import("../views/pages/bonus/BonusForm.vue"),
  },
  {
    path: "/bonus/import",
    name: "BonusImport",
    meta: {
      title: "Import Bonus",
      requiresAuth: true,
      endpoint: "bonus/import",
      baseRoute: "BonusList",
    },
    component: () => import("../views/pages/bonus/BonusImport.vue"),
  },
  {
    path: "/bonus/:id/edit",
    name: "BonusEdit",
    meta: {
      title: "View Bonus",
      requiresAuth: true,
      endpoint: "bonus",
      baseRoute: "BonusList",
    },
    component: () => import("../views/pages/bonus/BonusForm.vue"),
  },
  {
    path: "/withdraw",
    name: "WithdrawList",
    meta: {
      title: "List Withdraw",
      requiresAuth: true,
    },
    component: () => import("../views/pages/withdraw/WithdrawList.vue"),
  },
  {
    path: "/withdraw/create/:accountId?",
    name: "WithdrawCreate",
    meta: {
      title: "Create Withdraw",
      requiresAuth: true,
      endpoint: "withdraw",
      baseRoute: "WithdrawList",
    },
    component: () => import("../views/pages/withdraw/WithdrawForm.vue"),
  },
  {
    path: "/withdraw/:id/edit",
    name: "WithdrawEdit",
    meta: {
      title: "View Withdraw",
      requiresAuth: true,
      endpoint: "withdraw",
      baseRoute: "WithdrawList",
    },
    component: () => import("../views/pages/withdraw/WithdrawForm.vue"),
  },
  {
    path: "/transfer",
    name: "TransferList",
    meta: {
      title: "List Transfer",
      requiresAuth: true,
    },
    component: () => import("../views/pages/transfer/TransferList.vue"),
  },
  {
    path: "/transfer/create/:accountId?",
    name: "TransferCreate",
    meta: {
      title: "Create Transfer",
      requiresAuth: true,
      endpoint: "transfer",
      baseRoute: "TransferList",
    },
    component: () => import("../views/pages/transfer/TransferForm.vue"),
  },
  {
    path: "/transfer/:id/edit",
    name: "TransferEdit",
    meta: {
      title: "View Transfer",
      requiresAuth: true,
      endpoint: "transfer",
      baseRoute: "TransferList",
    },
    component: () => import("../views/pages/transfer/TransferForm.vue"),
  },
  {
    path: "/nexus/deposit",
    name: "NexusDepositList",
    meta: {
      title: "List Nexus Deposit",
      requiresAuth: true,
    },
    component: () => import("../views/pages/nexus/NexusDepositList.vue"),
  },
  {
    path: "/nexus/withdraw",
    name: "NexusWithdrawList",
    meta: {
      title: "List Nexus Withdraw",
      requiresAuth: true,
    },
    component: () => import("../views/pages/nexus/NexusWithdrawList.vue"),
  },
  {
    path: "/nexus/sync-setting",
    name: "NexusSyncSetting",
    meta: {
      title: "Nexus Sync Settings",
      requiresAuth: true,
    },
    component: () => import("../views/pages/nexus/NexusSyncSetting.vue"),
  },
  {
    path: "/nexus/sync-report",
    name: "NexusSyncReport",
    meta: {
      title: "Nexus Sync Report",
      requiresAuth: true,
    },
    component: () => import("../views/pages/nexus/NexusSyncReport.vue"),
  },
  {
    path: "/report/member-transaction",
    name: "ReportMemberTransaction",
    meta: {
      title: "Report Member Transaction",
      requiresAuth: true,
    },
    component: () =>
      import("../views/pages/report/ReportMemberTransaction.vue"),
  },
  {
    path: "/report/bank",
    name: "ReportBank",
    meta: {
      title: "Report Bank",
      requiresAuth: true,
    },
    component: () => import("../views/pages/report/ReportBank.vue"),
  },
  {
    path: "/migrate-member",
    name: "MigrateMemberList",
    meta: {
      title: "Migrate Member Data",
      requiresAuth: true,
    },
    component: () => import("../views/pages/migrate/MigrateMemberList.vue"),
  },
  {
    path: "/migrate-bank",
    name: "MigrateBankList",
    meta: {
      title: "Migrate Bank Data",
      requiresAuth: true,
    },
    component: () => import("../views/pages/migrate/MigrateBankList.vue"),
  },
  {
    path: "/search/:search",
    name: "SearchList",
    meta: {
      title: "Search Results",
      requiresAuth: true,
    },
    component: () => import("../views/pages/SearchList.vue"),
  },
  {
    path: "/profile",
    name: "Profile",
    meta: {
      title: "Your Profile",
      requiresAuth: true,
    },
    component: () => import("../views/pages/Profile.vue"),
  },
  {
    path: "/user-roles",
    name: "UserRoleList",
    meta: {
      title: "List User Roles",
      endpoint: "role",
      requiresAuth: true,
    },
    component: () => import("../views/pages/user-role/UserRoleList.vue"),
  },
  {
    path: "/user-role/create",
    name: "UserRoleCreate",
    meta: {
      title: "New User Role",
      requiresAuth: true,
      endpoint: "role",
      baseRoute: "UserRoleList",
    },
    component: () => import("../views/pages/user-role/UserRoleForm.vue"),
  },
  {
    path: "/user-role/:id/edit",
    name: "UserRoleEdit",
    meta: {
      title: "View User Role",
      requiresAuth: true,
      endpoint: "role",
      baseRoute: "UserRoleList",
    },
    component: () => import("../views/pages/user-role/UserRoleForm.vue"),
  },
  {
    path: "/export/member-transaction",
    name: "ExportMemberTransaction",
    meta: {
      title: "Export Member Transaction",
      requiresAuth: true,
    },
    component: () =>
      import("../views/pages/export/ExportMemberTransaction.vue"),
  },
  {
    path: "/export/bank-transaction",
    name: "ExportBankTransaction",
    meta: {
      title: "Export Bank Transaction",
      requiresAuth: true,
    },
    component: () => import("../views/pages/export/ExportBankTransaction.vue"),
  },
  {
    path: "/export/bank-balance",
    name: "ExportBankBalance",
    meta: {
      title: "Export Bank Balance",
      requiresAuth: true,
    },
    component: () => import("../views/pages/export/ExportBankBalance.vue"),
  },
  {
    path: "/export/bank-history",
    name: "ExportBankHistory",
    meta: {
      title: "Export Bank History",
      requiresAuth: true,
    },
    component: () => import("../views/pages/export/ExportBankHistory.vue"),
  },
  {
    path: "/export/crm",
    name: "ExportCrm",
    meta: {
      title: "Export CRM",
      requiresAuth: true,
    },
    component: () => import("../views/pages/export/ExportCRM.vue"),
  },
  {
    path: "/support-ticket",
    name: "SupportTicketList",
    meta: {
      title: "Support Ticket",
      endpoint: "ticket",
      requiresAuth: true,
    },
    component: () =>
      import("../views/pages/support-ticket/SupportTicketList.vue"),
  },
  {
    path: "/support-ticket/create",
    name: "SupportTicketCreate",
    meta: {
      title: "Create Support Ticket",
      endpoint: "ticket",
      requiresAuth: true,
      baseRoute: "SupportTicketList",
    },
    component: () =>
      import("../views/pages/support-ticket/SupportTicketForm.vue"),
  },
  {
    path: "/support-ticket/:id/view",
    name: "SupportTicketView",
    meta: {
      title: "View Support Ticket",
      endpoint: "ticket",
      requiresAuth: true,
      baseRoute: "SupportTicketList",
    },
    component: () =>
      import("../views/pages/support-ticket/SupportTicketForm.vue"),
  },
  {
    path: "/404",
    name: "NotFound",
    component: NotFound,
  },
  {
    path: "/unauthorized",
    name: "Unauthorized",
    component: Unauthorized,
  },
  {
    path: "*",
    component: NotFound,
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach((to, from, next) => {
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (!store.getters.isLoggedIn) {
      store.commit(
        "changeLoginMessage",
        "You have to be logged in to view that page."
      );
      next({
        name: "Login",
      });
    } else {
      if (to.meta.allowedRole) {
        if (!to.meta.allowedRole.includes(store.getters.getRole)) {
          next({
            name: "Unauthorized",
          });
        }
      }

      const currentTimestamp =
        new Date(
          new Date().toLocaleString("en-us", { timeZone: "Asia/Jakarta" })
        ).getTime() / 1000;

      const tokenExpiry = store.getters.getTokenExpiry;
      // const differenceInMinutes = (tokenExpiry - currentTimestamp) / 60;
      const loginStatus = store.getters.isLoggedIn;
      const refreshOn = store.getters.getTokenRefreshOn;
      const differenceInSeconds = tokenExpiry - currentTimestamp;

      if (loginStatus) {
        if (differenceInSeconds <= refreshOn) {
          store.dispatch("refresh").then(() => {
            next();
          });
          // .catch(() => {
          //   // Ngga perlu karena udah ada di main.js interceptors.
          // });
        } else {
          next();
        }
      }
    }
  } else {
    next();
  }

  store.commit("closeSidebar");
});

export default router;
